import { getApiBaseUrl } from "../config/runtimeConfig";
import type { Worker } from "../trabalhadores/trabalhadoresApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(ApiError.humanize(message));
    this.status = status;
  }

  // ASP.NET error responses arrive as a ProblemDetails/ValidationProblemDetails
  // JSON body; surface the human-readable message instead of the raw JSON string.
  private static humanize(message: string): string {
    const trimmed = message?.trim();
    if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return message;
    try {
      const parsed = JSON.parse(trimmed) as {
        detail?: string;
        title?: string;
        errors?: Record<string, string[]>;
      };
      const validationMessages = parsed.errors
        ? Object.values(parsed.errors).flat().filter(Boolean)
        : [];
      if (validationMessages.length > 0) return validationMessages.join(" ");
      return parsed.detail || parsed.title || message;
    } catch {
      return message;
    }
  }
}

// Uma linha da Folha de Horas (registo de horas numa obra ou local de texto livre).
export interface TimesheetEntry {
  id: number;
  workId: number | null;
  workName: string;
  locationText: string | null;
  locationLabel: string;
  workerId: number | null;
  workerName: string;
  workedOn: string;
  hours: number;
  notes: string | null;
  isValidated: boolean;
  validatedAt: string | null;
  created: string;
  lastModified: string;
}

export interface CreateTimesheetPayload {
  // Só respeitado quando o autor é Administrador; senão usa o próprio trabalhador.
  workerId?: number | null;
  workId?: number | null;
  locationText?: string | null;
  workedOn: string;
  hours: number;
  notes?: string | null;
}

export interface TimesheetFilter {
  workerId?: number | null;
  from?: string | null;
  to?: string | null;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  // Corpo vazio conta como null, não como erro. O ASP.NET não escreve corpo nenhum quando o
  // valor de um Ok(...) é null, portanto /api/workers/me responde 200 com zero bytes a quem
  // não tem trabalhador associado. Fazer response.json() a isso lança "Unexpected end of JSON
  // input" — e esse throw derrubava o Promise.all da Folha de Horas, levando atrás as obras e
  // os trabalhadores que já tinham chegado bem.
  const body = await response.text();
  return (body ? JSON.parse(body) : null) as T;
}

// Trabalhador associado à conta autenticada, ou null se não houver associação.
export function getMyWorker(): Promise<Worker | null> {
  return request<Worker | null>("/api/workers/me");
}

// Trabalhadores sobre os quais o utilizador pode lançar/validar horas:
// admin → todos; encarregado → ele próprio + a sua equipa; normal → só o próprio.
export function getTimesheetWorkers(): Promise<Worker[]> {
  return request<Worker[]>("/api/work-labor-entries/workers");
}

export function getTimesheet(filter: TimesheetFilter = {}): Promise<TimesheetEntry[]> {
  const params = new URLSearchParams();
  if (filter.workerId != null) params.set("workerId", String(filter.workerId));
  if (filter.from) params.set("from", filter.from);
  if (filter.to) params.set("to", filter.to);
  const query = params.toString();
  return request<TimesheetEntry[]>(
    `/api/work-labor-entries/mine${query ? `?${query}` : ""}`,
  );
}

export function createTimesheetEntry(
  payload: CreateTimesheetPayload,
): Promise<TimesheetEntry> {
  return request<TimesheetEntry>("/api/work-labor-entries/mine", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

// Encarregado/Admin valida (ou reverte) as horas de um trabalhador.
export function validateTimesheetEntry(
  id: number,
  isValidated: boolean,
): Promise<TimesheetEntry> {
  return request<TimesheetEntry>(`/api/work-labor-entries/${id}/validate`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ isValidated }),
  });
}

export async function deleteTimesheetEntry(id: number): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/work-labor-entries/mine/${id}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}
